import { useEffect } from "react";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Modal from "react-responsive-modal";
import { Loader } from 'google-maps';

import { CTA } from "@merchstores/shared/elements/Cta";
import { FormInput } from "@merchstores/shared/elements/FormInput";

import countriesInformation from "i18n-iso-countries/langs/en.json";

import { IMerchstoreShipToOffice } from "./index";
import "./styles.scss";
import { FormSelect } from "@merchstores/shared/elements/FormSelect";
import _ from "lodash";
import { testCheckoutShippingAddress } from "../../MerchStoreOrderCheckout";
import { toast } from "react-toastify";

export const MerchstoreShipToOffice = (props: IMerchstoreShipToOffice) => {
    const { register, handleSubmit, setValue, setError, clearErrors, reset, formState: { errors }, control } = useForm();

    const [modalOpen, setModalOpen] = useState(props.modalOpen);
    const [countries, setCountries] = useState([]);
    const [merchStore, setMerchStore] = useState(props.merchStore);
    const [defaultCountry, setDefaultCountry] = useState("");
    const [addressError, setAddressError] = useState(null);

    const loader = new Loader(process.env.REACT_APP_GOOGLE_API_KEY, { libraries: ['places'], language: 'en' });
    let autocomplete: any;
    let google: any;

    const zip = useWatch({
        control,
        name: "zip"
    });

    useEffect(() => {
        initAutocomplete();
    }, []);

    useEffect(() => {
        if (countriesInformation && countriesInformation.countries) {
            const countriesNames = Object.values(countriesInformation.countries);
            const countries = countriesNames.map(c => {
                let countryName = "";
                if (Array.isArray(c)) {
                    countryName = c[0];
                } else {
                    countryName = c;
                }
                if (countryName.toLowerCase() === "united states of america") {
                    countryName = "United States";
                }
                return { displayText: countryName, value: countryName };
            })
            setCountries(countries);
        }
    }, [countriesInformation])

    useEffect(() => {
        if (props.modalOpen !== modalOpen) {
            setModalOpen(props.modalOpen);
        }
    }, [props.modalOpen]);

    useEffect(() => {
        if (props.merchStore && props.merchStore !== merchStore) {
            setMerchStore(props.merchStore);
        }
    }, [props.merchStore]);

    useEffect(() => {
        if (zip) {
            const regex = /^\d{5}[-\s]?(?:\d{4})?$/gm;
            if (!regex.test(zip)) {
                setError("zip", { type: "invalid", message: `Please enter a valid Postal Code` });
            } else {
                clearErrors("zip");
            }
        }

    }, [zip]);

    useEffect(() => {
        if (countries) {
            setDefaultCountry("United States");
        }
    }, [countries]);

    const handleShipToOfficeAddressSave = async (data: any) => {
        try {
            const checkoutResponse = await testCheckoutShippingAddress([], {
                ...merchStore,
                officeAddress: data
            });
            if (checkoutResponse.errors && checkoutResponse.errors.length >= 1) {
                const errors = checkoutResponse.errors.map(err => err.message);                
                setAddressError(_.join(errors, "\n"))
            } else {
                setAddressError(null);
                if (props.handleShipToOfficeAddressSave) {
                    props.handleShipToOfficeAddressSave(data);
                }
            }

        } catch (err) {
            setAddressError("The shipping address doesn't have a valid format. Please update it before saving your Merchstore.");
            throw err;
        }
    }

    const initAutocomplete = async () => {
        google = await loader.load();
        const addressInput = document.querySelector('[name="address1"]') as HTMLInputElement;
        autocomplete = new google.maps.places.Autocomplete(
            addressInput
        );
        autocomplete.setFields(["address_component"]);
        autocomplete.addListener("place_changed", fillInAddress);
    }

    function fillInAddress() {
        const place = autocomplete.getPlace();

        let address1 = "";
        let address2 = "";
        let city = "";
        let province = "";
        let zip = "";
        let country = "";

        const addressParts = [];

        if (place && place.address_components) {
            for (const component of place.address_components) {
                const addressType = component.types[0];
                addressParts[addressType] = component.long_name;
            }
            for (const addressType in addressParts) {
                switch (addressType) {
                    case "street_number":
                    case "route":
                        address1 += addressParts[addressType] + ' ';
                        break;
                    case "subpremise":
                        address2 += addressParts[addressType];
                        break;
                    case "locality":
                        city += addressParts[addressType];
                        break;
                    case "administrative_area_level_1":
                        province += addressParts[addressType];
                        break;
                    case "postal_code":
                        zip += addressParts[addressType];
                        break;
                    case "country":
                        country += addressParts[addressType];
                        break;
                    default:
                        break;
                }
            }

            setValue("address1", address1);
            setValue("address2", address2);
            setValue("city", city);
            setValue("province", province);
            setValue("zip", zip);
            setValue("country", country);

            // Wait until suggestions are rendered to hide them 
            setTimeout(() => {
                const div = document.querySelectorAll('.pac-container')[0] as HTMLElement;
                div.style.display = "none";

            }, 500)
        }
    }

    const onCloseModal = () => {
        setModalOpen(false);
        if (props.onCancelShipToOffice) {
            props.onCancelShipToOffice();
        }
    }

    return <>
        <Modal open={modalOpen} classNames={{ modal: "ship-to-office-modal" }} onClose={onCloseModal} center>
            <h2>Ship to Office</h2>
            <h3 className="font-bold">Office Address</h3>
            <form onSubmit={handleSubmit(handleShipToOfficeAddressSave)} className="w-full" id="create-edit-shipping-form">
                <FormInput
                    label="Company"
                    name="company"
                    errors={errors}
                    register={register}
                    required={true}
                    type="text"
                    defaultValue={merchStore && merchStore.officeAddress ? merchStore.officeAddress.company : ""} />
                <div className="flex two-inputs-50-50">
                    <FormInput
                        label="First name"
                        name="firstName"
                        errors={errors}
                        register={register}
                        required={true}
                        type="text"
                        defaultValue={merchStore && merchStore.officeAddress ? merchStore.officeAddress.firstName : ""} />
                    <FormInput
                        label="Last name"
                        name="lastName"
                        errors={errors}
                        register={register}
                        required={false}
                        type="text"
                        defaultValue={merchStore && merchStore.officeAddress ? merchStore.officeAddress.lastName : ""} />
                </div>

                <div className="flex two-inputs-60-40">
                    <FormInput
                        label="Street Address"
                        name="address1"
                        errors={errors}
                        register={register}
                        required={true}
                        type="text"
                        defaultValue={merchStore && merchStore.officeAddress ? merchStore.officeAddress.address1 : ""} />
                    <FormInput
                        label="Apt, Suite, etc."
                        name="address2"
                        errors={errors}
                        register={register}
                        required={false}
                        type="text"
                        defaultValue={merchStore && merchStore.officeAddress ? merchStore.officeAddress.address2 : ""} />
                </div>

                <div className="flex two-inputs-50-50">
                    <FormInput
                        label="City"
                        name="city"
                        errors={errors}
                        register={register}
                        required={true}
                        type="text"
                        defaultValue={merchStore && merchStore.officeAddress ? merchStore.officeAddress.city : ""} />
                    <FormInput
                        label="State"
                        name="province"
                        errors={errors}
                        register={register}
                        required={true}
                        type="text"
                        defaultValue={merchStore && merchStore.officeAddress ? merchStore.officeAddress.province : ""} />
                </div>

                <div className="flex two-inputs-60-40">
                    <FormSelect
                        label="Country"
                        name="country"
                        errors={errors}
                        register={register}
                        setValue={setValue}
                        options={countries}
                        default={defaultCountry}
                        selectStyle="two" />
                    <FormInput
                        label="Postal code"
                        name="zip"
                        errors={errors}
                        register={register}
                        required={true}
                        type="text"
                        defaultValue={merchStore && merchStore.officeAddress ? merchStore.officeAddress.zip : ""} />
                </div>
                {addressError && <p className="error pl-2 pb-2">{addressError}</p>}

                <div className="flex justify-between">
                    <CTA size="large"
                        type="secondary" onClick={onCloseModal} classes="mr-16">Cancel</CTA>
                    <CTA size="large" type="primary" formSubmit={true} disabled={!_.isEmpty(errors)}>Save</CTA>
                </div>
            </form>
        </Modal>
    </>
}